import React from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import MarketingPage, { PageHero } from "../../components/marketing/MarketingPage";

const creditPacks = [
  {
    name: "Starter",
    audience: "Best for trying things out",
    price: "$5",
    credits: "100 credits",
    note: "~$0.05 per credit",
    features: [
      "Instant delivery via Stripe checkout",
      "Works across Collavue features",
      "Email receipt",
    ],
  },
  {
    name: "Growth",
    audience: "Good value for active users",
    price: "$10",
    credits: "250 credits",
    note: "~$0.04 per credit",
    features: [
      "Instant delivery via Stripe checkout",
      "Works across Collavue features",
      "Priority email support",
    ],
  },
  {
    name: "Scale",
    audience: "Best value",
    price: "$20",
    credits: "500 credits",
    note: "~$0.04 per credit",
    features: [
      "Instant delivery via Stripe checkout",
      "Works across Collavue features",
      "Priority email support",
    ],
    highlight: true,
  },
];

export default function Pricing() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Collavue",
    description: "AI lead discovery and reply agent for B2B teams.",
    offers: creditPacks.map((p) => ({
      "@type": "Offer",
      name: p.name,
      price: p.price.replace("$", ""),
      priceCurrency: "USD",
    })),
  };

  return (
    <MarketingPage
      seo={{
        title: "Pricing — Buy Collavue credits",
        description:
          "Pay-as-you-go Collavue credits. Start at $5 for 100 credits, scale to 250 or 500 credit packs. No subscriptions — one-time Stripe checkout.",
        path: "/pricing",
        keywords:
          "Collavue pricing, AI lead generation credits, X lead tool price, Twitter lead tool price, LinkedIn lead tool price, Reddit lead tool price, social listening credits",
        schema,
      }}
    >
      <PageHero
        eyebrow="Pricing"
        title="Pay only for what you"
        italicWord="use."
        description="No subscriptions. Buy a one-time credit pack and use it across every Collavue feature."
      />

      <section className="bg-white border-t border-[#e3dccb]">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-6">
          {creditPacks.map((p) => (
            <div
              key={p.name}
              className={`rounded-2xl border p-7 bg-white relative ${
                p.highlight
                  ? "border-[#2d5bff] shadow-[0_30px_80px_-40px_rgba(45,91,255,0.5)]"
                  : "border-[#e3dccb]"
              }`}
            >
              {p.highlight && (
                <span className="absolute -top-3 right-6 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#2d5bff] text-white">
                  <Sparkles className="w-3.5 h-3.5" />
                  Best value
                </span>
              )}
              <h3 className="font-display text-2xl text-[#0a1736] font-semibold">{p.name}</h3>
              <p className="text-[#5a6478] mt-2 text-sm">{p.audience}</p>
              <div className="mt-5 flex items-baseline gap-2">
                <span className="font-display text-5xl text-[#0a1736] font-semibold">{p.price}</span>
              </div>
              <div className="mt-2 text-[#1c2a4d] font-medium">{p.credits}</div>
              <div className="text-xs text-[#5a6478] mt-1">{p.note}</div>
              <ul className="mt-6 space-y-2">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#1c2a4d]">
                    <Check className="w-4 h-4 text-[#2d5bff] mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={`/billing?pack=${encodeURIComponent(p.name)}`}
                className={`mt-7 inline-flex w-full justify-center px-5 py-2.5 rounded-md font-medium transition-colors ${
                  p.highlight
                    ? "bg-[#0a1736] text-white hover:bg-[#1c2a4d]"
                    : "bg-white text-[#0a1736] border border-[#e3dccb] hover:border-[#0a1736]"
                }`}
              >
                Buy credits
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#faf6ee] border-t border-[#e3dccb]">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <h2 className="font-display text-3xl md:text-4xl text-[#0a1736] font-semibold">FAQ</h2>
          <div className="mt-6 space-y-4">
            {[
              {
                q: "How do credits work?",
                a: "Each Collavue action — analyzing a website, generating a campaign, drafting a reply — costs a small number of credits. Buy a pack once and spend them whenever you need.",
              },
              {
                q: "Do credits expire?",
                a: "No. Credits stay in your account until you use them.",
              },
              {
                q: "Can I buy more than one pack?",
                a: "Yes. You can top up at any time from your billing page. Larger packs offer a better per-credit rate.",
              },
              {
                q: "How is payment handled?",
                a: "Checkout is processed by Stripe. You'll receive an email receipt and credits land in your account immediately after payment.",
              },
            ].map((f) => (
              <div key={f.q} className="rounded-xl border border-[#e3dccb] bg-white p-5">
                <h3 className="font-semibold text-[#0a1736]">{f.q}</h3>
                <p className="text-[#5a6478] mt-2 text-sm">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </MarketingPage>
  );
}
